import type {
  VoiceNavSessionContextResponse,
  VoiceNavPendingOpenModalityWorkflow,
} from '../api/apiConfig'

import type { ChatMessage } from '../types/chat'

import { SESSION_ACTIVE_PATIENT_DEBUG_MESSAGE_ID } from './sessionActivePatientDebug'
import { SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID } from './pendingUiReplayOutcome'

/** Stable id so repeated hydrates replace the same bubble instead of stacking. */
export const SESSION_PENDING_COMMANDS_DEBUG_MESSAGE_ID = 'voicnav:sessionPendingCommandsDebug'

export const SESSION_HYDRATE_DEBUG_MESSAGE_IDS = [
  SESSION_PENDING_COMMANDS_DEBUG_MESSAGE_ID,
  SESSION_ACTIVE_PATIENT_DEBUG_MESSAGE_ID,
] as const

export function isSessionHydrateDebugMessageId(id: string): boolean {
  return (SESSION_HYDRATE_DEBUG_MESSAGE_IDS as readonly string[]).includes(id)
}

/** Client-only post-refresh status rows (not shown in chat; session/Redis load still runs). */
export function isPostRefreshUiDebugChatMessageId(id: string): boolean {
  if (isSessionHydrateDebugMessageId(id)) return true
  if (id === SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID) return true
  if (id.startsWith('reload-debug-')) return true
  return false
}

export function withoutPostRefreshUiDebugMessages(messages: readonly ChatMessage[]): ChatMessage[] {
  return messages.filter((m) => !isPostRefreshUiDebugChatMessageId(m.id))
}

export function withoutSessionHydrateDebugMessages(messages: readonly ChatMessage[]): ChatMessage[] {
  return withoutPostRefreshUiDebugMessages(messages)
}

function formatDeferredOpenEcg(ctx: VoiceNavSessionContextResponse): string {
  const dog = ctx.deferred_open_ecg
  if (!dog || !dog.pending) return ''
  const flags: string[] = []
  if (dog.expired) flags.push('expired')
  if (dog.replay_ready) flags.push('replay_ready')
  const flagStr = flags.length ? flags.join(', ') : 'pending'
  return `; deferred_open_ecg: ${flagStr}`
}

function formatPendingOpenModalityWorkflow(
  wf: VoiceNavPendingOpenModalityWorkflow | null | undefined,
  ctx: VoiceNavSessionContextResponse,
): string {
  if (!wf) return ''
  if (
    wf.kind !== 'open_modality_for_patient' &&
    wf.kind !== 'view_prescription_for_patient' &&
    wf.kind !== 'sports_noc_for_patient' &&
    wf.kind !== 'renew_ordonnance_for_patient'
  ) return ''
  const nm = (wf.target_patient_name || '').trim() || '?'
  const reg =
    ctx.active_patient?.patient_name &&
    nm &&
    ctx.active_patient.patient_name.trim().toLowerCase() !== nm.toLowerCase()
      ? ` (session chart: ${ctx.active_patient.patient_name.trim()})`
      : ''
  const wait = wf.awaiting_switch_confirm
    ? ' — step 1: confirm switch; step 2: open patient; then ``deferred_open_ecg`` in session Redis + POST pending-ui-replay'
    : wf.kind === 'view_prescription_for_patient'
      ? ' — will open prescription after patient resolution (no switch confirm)'
      : wf.kind === 'sports_noc_for_patient'
        ? ' — will start sports certificate flow after patient resolution (no switch confirm)'
        : wf.kind === 'renew_ordonnance_for_patient'
          ? ' — will renew prescription after patient resolution (no switch confirm)'
        : ' — will open exam after patient resolution (no switch confirm)'
  return `; queued modality step: ${wf.then_open} for "${nm}"${reg}${wait}`
}

/** Last assistant row that still expects modality replay (matches deferred ECG pipeline). */
export function latestPendingModalityCommandFromChat(messages: readonly ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant') continue
    const pm = m.pendingModalityExam
    if (!pm) continue
    if (pm.status === 'pending' || pm.replayPending === true) {
      const mod = (pm.modality || 'exam').trim().toLowerCase() || 'exam'
      return `open_modality_exam:${mod}`
    }
  }
  return null
}

/**
 * One-line status after full page reload: ``deferred_open_ecg``, Redis ``pending_ui_replay``, and transcript rows
 * (``pending_modality_exam`` survives in chat history even when the server queue is already idle).
 * Temporary UX aid — message is not written to Redis chat history.
 */
export function buildSessionPendingCommandsHydrateChatText(
  ctx: VoiceNavSessionContextResponse | null,
  restoredMessages: readonly ChatMessage[],
): string {
  const chatCmd = latestPendingModalityCommandFromChat(restoredMessages)

  if (!ctx) {
    const chatPart = chatCmd ? `; chat transcript: ${chatCmd}` : ''
    return `[Session] After refresh — Redis: (could not load session context)${chatPart}`
  }
  const dogPart = formatDeferredOpenEcg(ctx)
  const pr = ctx.pending_ui_replay
  if (!pr) {
    const chatPart = chatCmd ? `; chat transcript: ${chatCmd}` : ''
    const wfPart = formatPendingOpenModalityWorkflow(ctx.pending_open_modality_workflow, ctx)
    return `[Session] After refresh — Redis: none${dogPart}${wfPart}${chatPart}`
  }
  const cmd = pr.pending_command
  const flags: string[] = []
  if (pr.expired) flags.push('expired')
  if (pr.deferred) flags.push('deferred')
  if (pr.replay_ready) flags.push('replay_ready')
  if (pr.armed) flags.push('armed')
  const flagStr = flags.length ? flags.join(', ') : 'idle'
  let redisPart: string
  if (!pr.deferred && !pr.expired) {
    redisPart = `Redis: none (replay: ${flagStr})`
  } else {
    const cmdStr = cmd ? ` — ${cmd}` : ''
    redisPart = `Redis: ${flagStr}${cmdStr}`
  }
  const chatPart = chatCmd ? `; chat transcript: ${chatCmd}` : ''
  const wfPart = formatPendingOpenModalityWorkflow(ctx.pending_open_modality_workflow, ctx)
  return `[Session] After refresh — ${redisPart}${dogPart}${wfPart}${chatPart}`
}
